import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
  Typography,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { fileService } from '../../services/fileService';
import type { AgentSessionFileDto, FileCategory } from '../../types/file';
import { useNotificationStore } from '../../store/notificationStore';
import { FilePreview } from './FilePreview';

export interface FileListProps {
  sessionId: string;
  refreshKey?: number;
}

const categoryLabel = (c: FileCategory) => {
  switch (c) {
    case 0:
      return 'Input';
    case 1:
      return 'Output';
    case 2:
      return 'Generated';
    case 3:
      return 'Attachment';
    case 4:
      return 'Temporary';
    case 5:
      return 'Artifact';
    case 6:
      return 'Log';
    default:
      return 'Unknown';
  }
};

const formatSize = (bytes: number) => {
  if (!bytes || bytes <= 0) return '0 B';
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
};

/**
 * File list component for a session.
 */
export const FileList = ({ sessionId, refreshKey }: FileListProps) => {
  const { addNotification } = useNotificationStore();

  const [files, setFiles] = useState<AgentSessionFileDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<AgentSessionFileDto | null>(null);

  const sorted = useMemo(() => {
    return [...files].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [files]);

  const load = async () => {
    setLoading(true);
    try {
      const result = await fileService.getSessionFiles(sessionId);
      setFiles(result);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load files';
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, refreshKey]);

  const handlePreview = (f: AgentSessionFileDto) => {
    setSelectedFile(f);
    setPreviewOpen(true);
  };

  const handleDownload = async (f: AgentSessionFileDto) => {
    try {
      const resp = await fileService.downloadFileBlob(f.id);
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = f.fileName || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to download file';
      addNotification(msg, 'error');
    }
  };

  const handleDelete = async (f: AgentSessionFileDto) => {
    if (!window.confirm(`Delete file "${f.fileName}"?`)) return;
    try {
      await fileService.deleteFile(f.id);
      addNotification('File deleted', 'success');
      await load();
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to delete file';
      addNotification(msg, 'error');
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : sorted.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          No files yet.
        </Typography>
      ) : (
        <List dense disablePadding>
          {sorted.slice(0, 10).map((f) => (
            <ListItem key={f.id} divider>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {f.fileName}
                    </Typography>
                    <Chip size="small" label={categoryLabel(f.category)} variant="outlined" />
                    <Chip size="small" label={`v${f.version}`} variant="outlined" />
                  </Box>
                }
                secondary={`${formatSize(f.fileSizeBytes)} • ${new Date(f.createdAt).toLocaleString()}`}
              />
              <ListItemSecondaryAction>
                <Tooltip title="Preview">
                  <IconButton size="small" onClick={() => handlePreview(f)}>
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download">
                  <IconButton size="small" onClick={() => handleDownload(f)}>
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => handleDelete(f)} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
          {sorted.length > 10 && (
            <Box sx={{ px: 1, py: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Showing 10 of {sorted.length} files.
              </Typography>
            </Box>
          )}
        </List>
      )}

      <FilePreview open={previewOpen} file={selectedFile} onClose={() => setPreviewOpen(false)} />
    </Box>
  );
};

